/**
 * The campaign schedule observability routes' typed edge (platform design §6.4, §6.5;
 * `api/src/socialsrc_api/schemas/schedule.py`, `routers/schedule.py` — CO-5, merged).
 *
 * Written here, field for field from the pydantic models, rather than imported: this is a
 * `web`-owned contract on a route this task does not block on landing again.
 *
 * `state` and `reason` are read from `core`'s own enums at the point CO-5 was written --
 * `socialsrc_core.ledger.states.LedgerState` and `socialsrc_core.ledger.degradation.
 * DegradationReason` -- and quoted verbatim here, because a quoted enum decays.
 */

import { z } from 'zod';

export const ledgerStateSchema = z.enum(['planned', 'dispatched', 'done', 'failed']);
export type LedgerStateValue = z.infer<typeof ledgerStateSchema>;

export const degradationReasonSchema = z.enum([
  'budget_shed',
  'budget_window',
  'budget_hold',
  'collection_cancelled',
  'provider_unavailable',
  'provider_not_found',
  'collection_failed',
]);
export type DegradationReasonValue = z.infer<typeof degradationReasonSchema>;

/** The two platforms this estate tracks (`socialsrc_core.db.enums.Platform`); a scheduled slot's
 *  account predates tracking or was removed when neither field the join needs survived. */
export const platformSchema = z.enum(['instagram', 'tiktok']);
export type PlatformValue = z.infer<typeof platformSchema>;

export const scheduledSlotSchema = z.object({
  scheduled_slot: z.iso.datetime({ offset: true }),
  job_type: z.string(),
  target_id: z.string(),
  account_label: z.string().nullable(),
  account_handle: z.string().nullable(),
  platform: platformSchema.nullable(),
  follower_count: z.number().int().nullable(),
  state: ledgerStateSchema,
  attempts: z.number().int(),
  credits_used: z.number().int().nullable(),
});
export type ScheduledSlot = z.infer<typeof scheduledSlotSchema>;

export const agendaSchema = z.object({
  earliest: z.iso.datetime({ offset: true }).nullable(),
  latest: z.iso.datetime({ offset: true }).nullable(),
  slots: z.array(scheduledSlotSchema),
  /** The earliest planned, unfunded slot the hot ledger holds -- `null` when every planned slot
   *  is funded. Not scoped to the request's own window: a campaign can be missing credits for a
   *  slot outside whatever range the caller asked to see. */
  first_missed_slot: z.iso.datetime({ offset: true }).nullable(),
});
export type Agenda = z.infer<typeof agendaSchema>;

export const failureSchema = z.object({
  scheduled_slot: z.iso.datetime({ offset: true }),
  recorded_at: z.iso.datetime({ offset: true }),
  job_type: z.string(),
  target_id: z.string(),
  account_label: z.string().nullable(),
  account_handle: z.string().nullable(),
  reason: degradationReasonSchema,
});
export type Failure = z.infer<typeof failureSchema>;

export const failuresSchema = z.object({ failures: z.array(failureSchema) });

export const volumeBucketSchema = z.object({
  hour: z.iso.datetime({ offset: true }),
  credits_used: z.number().int(),
  posts: z.number().int(),
  comments: z.number().int(),
});
export type VolumeBucket = z.infer<typeof volumeBucketSchema>;

/** The methods `AmplificationEdge.method` can carry (`socialsrc_core.db.enums.DetectionMethod`),
 *  quoted verbatim here for the same reason as `degradationReasonSchema` above. */
export const detectionMethodSchema = z.enum([
  'FINGERPRINT_PHASH',
  'FINGERPRINT_ORB',
  'ACCOUNT_ID_MATCH',
  'HANDLE_MATCH',
  'AUDIO_ID_MATCH',
]);
export type DetectionMethodValue = z.infer<typeof detectionMethodSchema>;

export const audioSourceSchema = z.object({
  post_id: z.string(),
  url: z.string().nullable(),
  handle: z.string().nullable(),
  published_at: z.iso.datetime({ offset: true }),
});
export type AudioSource = z.infer<typeof audioSourceSchema>;

export const audioMatchSchema = z.object({
  source_post_id: z.string(),
  amplifier_account_id: z.string(),
  amplifier_handle: z.string().nullable(),
  evidence_ref: z.string().nullable(),
  occurred_at: z.iso.datetime({ offset: true }).nullable(),
  observed_at: z.iso.datetime({ offset: true }),
  method: detectionMethodSchema,
  confidence: z.number().nullable(),
});
export type AudioMatch = z.infer<typeof audioMatchSchema>;

export const audioTrackSchema = z.object({
  id: z.string(),
  platform: platformSchema,
  title: z.string().nullable(),
  original: z.boolean(),
  first_seen_at: z.iso.datetime({ offset: true }),
  last_lookup_at: z.iso.datetime({ offset: true }).nullable(),
  last_reuse_count: z.number().int().nullable(),
  next_lookup_at: z.iso.datetime({ offset: true }).nullable(),
  sources: z.array(audioSourceSchema),
  matches: z.array(audioMatchSchema),
});
export type AudioTrack = z.infer<typeof audioTrackSchema>;

export const audioTracksSchema = z.object({ tracks: z.array(audioTrackSchema) });

export const collectionSummarySchema = z.object({
  completed: z.number().int().nullable(),
  failed: z.number().int().nullable(),
  credits_used: z.number().int().nullable(),
  window: z.literal('closed').nullable(),
  /** The trailing 24 h from the request's own clock, one bucket per hour, independent of
   *  `window`. */
  volume: z.array(volumeBucketSchema),
});
export type CollectionSummary = z.infer<typeof collectionSummarySchema>;
